export const makeChildKey = (parentKey, itemId, childModifierId) =>
  `${parentKey}|${itemId}|${childModifierId}`;

export const getGroupSelection = (selection, groupKey) => selection[groupKey] || {};

export const getSelectedItemIds = (selection, groupKey) =>
  Object.keys(getGroupSelection(selection, groupKey));

export const selectRadio = (selection, groupKey, itemId) => ({
  ...selection,
  [groupKey]: { [itemId]: 1 },
});

export const toggleCheckbox = (selection, groupKey, itemId) => {
  const group = { ...getGroupSelection(selection, groupKey) };

  if (group[itemId]) {
    delete group[itemId];
  } else {
    group[itemId] = 1;
  }

  return { ...selection, [groupKey]: group };
};

export const setQuantity = (selection, groupKey, itemId, quantity) => ({
  ...selection,
  [groupKey]: { ...getGroupSelection(selection, groupKey), [itemId]: quantity },
});

// multiMax caps how many of a single item can be taken. When a modifier's items use it,
// minSelections/maxSelections are clamped to the combined multiMax capacity so the group
// never requires or advertises more selections than the items actually allow.
export const getEffectiveBounds = (modifier) => {
  const { minSelections = 0, maxSelections, modifierItems = [] } = modifier;
  const hasMultiMax = modifierItems.some((item) => item.multiMax);

  if (!hasMultiMax) return { minSelections, maxSelections };

  const capacity = modifierItems.reduce((sum, item) => sum + (item.multiMax ?? 1), 0);

  return {
    minSelections: Math.min(minSelections, capacity),
    maxSelections: Math.min(maxSelections, capacity),
  };
};

const seedGroup = (modifier, groupKey) => {
  const items = modifier.modifierItems || [];

  if (modifier.component === 'RADIO' && items.length) {
    const [first] = items;
    const childEntries = (first.modifiers || []).reduce(
      (acc, child) => ({
        ...acc,
        ...seedGroup(child, makeChildKey(groupKey, first.itemId, child.modifierId)),
      }),
      {},
    );
    return { [groupKey]: { [first.itemId]: 1 }, ...childEntries };
  }

  return { [groupKey]: {} };
};

export const buildInitialSelection = (modifiers = []) =>
  modifiers.reduce(
    (acc, modifier) => ({ ...acc, ...seedGroup(modifier, modifier.modifierId) }),
    {},
  );

export const collectActiveGroups = (modifiers = [], selection) => {
  const groups = [];

  const visit = (mods, makeKey, multiplier) => {
    mods.forEach((modifier) => {
      const groupKey = makeKey(modifier);
      const selectedIds = getSelectedItemIds(selection, groupKey);
      groups.push({ groupKey, modifier, selectedIds, multiplier });

      (modifier.modifierItems || []).forEach((item) => {
        if (selectedIds.includes(item.itemId) && item.modifiers?.length) {
          const itemQuantity = getGroupSelection(selection, groupKey)[item.itemId] || 1;
          visit(
            item.modifiers,
            (child) => makeChildKey(groupKey, item.itemId, child.modifierId),
            multiplier * itemQuantity,
          );
        }
      });
    });
  };

  visit(modifiers, (modifier) => modifier.modifierId, 1);
  return groups;
};

const groupSelectedCount = (selection, groupKey) =>
  Object.values(getGroupSelection(selection, groupKey)).reduce((sum, qty) => sum + qty, 0);

export const computeValidity = (modifiers = [], selection) =>
  collectActiveGroups(modifiers, selection).every(({ groupKey, modifier }) => {
    if (modifier.maxSelections === 1) return true;
    const { minSelections, maxSelections } = getEffectiveBounds(modifier);
    const count = groupSelectedCount(selection, groupKey);
    return count >= minSelections && count <= maxSelections;
  });

export const computeTotal = (modifiers = [], selection) =>
  collectActiveGroups(modifiers, selection).reduce((total, { groupKey, modifier, multiplier }) => {
    const group = getGroupSelection(selection, groupKey);
    const groupTotal = Object.entries(group).reduce((sum, [itemId, quantity]) => {
      const menuItem = (modifier.modifierItems || []).find((item) => item.itemId === itemId);
      return sum + (menuItem?.itemPrice || 0) * quantity;
    }, 0);
    return total + groupTotal * multiplier;
  }, 0);

const buildModifier = (modifier, groupKey, selection, isNested) => {
  const isRadio = modifier.component === 'RADIO';
  const group = getGroupSelection(selection, groupKey);

  const modifierItems = Object.entries(group).map(([itemId, quantity]) => {
    const menuItem = (modifier.modifierItems || []).find((item) => item.itemId === itemId);
    const nested = (menuItem?.modifiers || [])
      .map((child) =>
        buildModifier(child, makeChildKey(groupKey, itemId, child.modifierId), selection, true),
      )
      .filter((built) => built.modifierItems.length > 0);

    // Top-level radio selections are sent as bare ids (legacy contract). Everything else —
    // checkboxes and ANY nested selection — must be an object with modifierItemId + quantity
    if (isRadio && !nested.length && !isNested) {
      return itemId;
    }

    return {
      modifierItemId: itemId,
      quantity,
      ...(nested.length ? { modifiers: nested } : {}),
    };
  });

  return { modifierId: modifier.modifierId, modifierItems };
};

export const buildPayload = (modifiers = [], selection) =>
  modifiers
    .map((modifier) => buildModifier(modifier, modifier.modifierId, selection, false))
    .filter((built) => built.modifierItems.length > 0);
